import styles from './RegularButton.module.css';

import React from 'react';
import classNames from 'classnames';

const COMPONENT_NAME = 'RegularButton'; // Название компонента для использования в логах

const COLOR_MAP = {
  primary: 'var(--color-brand)',
  accent: 'var(--color-accent)',
  error: 'var(--color-error)',
  secondary: 'var(--color-secondary)',
};

const ALLOWED_VARIANTS = ['default', 'outlined', 'flat'];

/**
 * **RegularButton** — это компонент обычной кнопки с текстом, поддерживающий различные стили, цвета и подсказки.
 * Также позволяет добавлять иконки после текста через проп appendIcon.
 *
 * ### Описание параметров:
 *
 * @param {string} text - Текст кнопки.
 * @param {'primary'|'accent'|'error'|'secondary'} [color='primary'] - Цвет кнопки.
 *    Допустимые значения:
 *    - `'primary'` — основной цвет.
 *    - `'accent'` — акцентный цвет.
 *    - `'error'` — цвет ошибки.
 *    - `'secondary'` — системный доп. цвет.
 * @param {'default'|'outlined'|'flat'} [variant='default'] - Вариация стиля кнопки.
 *    Допустимые значения:
 *    - `'default'` — стандартная кнопка.
 *    - `'outlined'` — кнопка с контуром.
 *    - `'flat'` — "плоская" кнопка (с фоном).
 * @param {React.ReactNode} [appendIcon] - Дополнительный элемент, например иконка, отображаемый после текста кнопки.
 * @param {string} [className] - Дополнительные классы для кастомной стилизации кнопки.
 * @param {function} onClick - Обработчик клика (функция, вызываемая при клике на кнопку).
 * @param {boolean} [disabled=false] - Состояние блокировки кнопки.
 *
 * ### Пример использования:
 * ```jsx
 * import RegularButton from './RegularButton';
 * import { FaArrowRight } from 'react-icons/fa';
 *
 * <RegularButton
 *   text="Кликни"
 *   className="custom-button"
 *   appendIcon={<FaArrowRight />}
 *   color="accent"
 *   variant="outlined"
 *   onClick={() => console.log('Клик')}
 * />
 * ```
 */
const RegularButton = ({
  className,
  text,
  color = 'primary',
  variant = 'default',
  appendIcon,
  onClick,
  disabled = false,
}) => {
  let resolvedColor = COLOR_MAP[color];

  // Проверка и логирование для пропса color
  if (!resolvedColor) {
    console.warn(
      `[${COMPONENT_NAME}] Invalid value for prop "color": "${color}". Defaulting to "primary".`,
    );
    resolvedColor = COLOR_MAP.primary;
  }

  // Проверка и логирование для пропса variant
  if (!ALLOWED_VARIANTS.includes(variant)) {
    console.warn(
      `[${COMPONENT_NAME}] Invalid value for prop "variant": "${variant}". Defaulting to "default".`,
    );
    variant = 'default';
  }

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={classNames(
        className,
        styles.regularButton,
        styles[`color-${color}`], // Динамические классы для цвета
        styles[`variant-${variant}`], // Динамические классы для варианта
        {
          [styles.disabled]: disabled,
        },
      )}
      style={{ color: variant !== 'flat' ? resolvedColor : 'white' }} // Дополнительная логика цвета текста
    >
      {/* Текст кнопки */}
      {text}
      {/* Иконка после текста */}
      {appendIcon}
    </button>
  );
};

export default RegularButton;
